import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Autocomplete, useJsApiLoader } from "@react-google-maps/api";
import { useTheme } from "../../context/ThemeContext";
import NeonButton from "../ResponsiveButton/NeonButton.jsx";
import { MapPin, Calculator } from "lucide-react";

/* ========================
   VAKIOT JA APUTYÖKALUT
   ======================== */

const GMAPS_LOADER_ID = "gmap";
const GMAPS_LIBS = ["places"];
const LS_KEY = "priceCalcWizard.v2";
const KM_PRICE = 0.59; // kiinteä €/km

const REGIONS_FI = [
  "Uusimaa","Varsinais-Suomi","Satakunta","Kanta-Häme","Pirkanmaa","Päijät-Häme",
  "Kymenlaakso","Etelä-Karjala","Etelä-Savo","Pohjois-Savo","Pohjois-Karjala","Keski-Suomi",
  "Etelä-Pohjanmaa","Pohjanmaa","Keski-Pohjanmaa","Pohjois-Pohjanmaa","Kainuu","Lappi","Ahvenanmaa"
];

const FIXED_SETS = ["1x60min", "2x45min", "3x45min"];

const cx = (...c) => c.filter(Boolean).join(" ");
const numOrNull = (v) => {
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

const norm = (s) =>
  String(s || "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const REGION_CANON = {
  uusimaa: "Uusimaa", nyland: "Uusimaa",
  varsinaissuomi: "Varsinais-Suomi", southwestfinland: "Varsinais-Suomi", egentligafinland: "Varsinais-Suomi",
  satakunta: "Satakunta",
  kantahame: "Kanta-Häme", tavastiaproper: "Kanta-Häme",
  pirkanmaa: "Pirkanmaa",
  paijathame: "Päijät-Häme",
  kymenlaakso: "Kymenlaakso", kymmenedalen: "Kymenlaakso",
  etelakarjala: "Etelä-Karjala", southkarelia: "Etelä-Karjala",
  etelasavo: "Etelä-Savo", southsavo: "Etelä-Savo",
  pohjoissavo: "Pohjois-Savo", northsavo: "Pohjois-Savo",
  pohjoiskarjala: "Pohjois-Karjala", northkarelia: "Pohjois-Karjala",
  keskisuomi: "Keski-Suomi", centralfinland: "Keski-Suomi",
  etelapohjanmaa: "Etelä-Pohjanmaa", southostrobothnia: "Etelä-Pohjanmaa",
  pohjanmaa: "Pohjanmaa", ostrobothnia: "Pohjanmaa",
  keskipohjanmaa: "Keski-Pohjanmaa", centralostrobothnia: "Keski-Pohjanmaa",
  pohjoispohjanmaa: "Pohjois-Pohjanmaa", northostrobothnia: "Pohjois-Pohjanmaa",
  kainuu: "Kainuu",
  lappi: "Lappi", lapland: "Lappi", lappland: "Lappi",
  ahvenanmaa: "Ahvenanmaa", aland: "Ahvenanmaa",
};
const toCanon = (r) => REGION_CANON[norm(r)] || r;

/** Luo hinnasto: { [maakunta]: { [setLabel]: "hinta" } } */
function createEmptyPriceMap() {
  const obj = {};
  for (const r of REGIONS_FI) {
    obj[r] = {};
    for (const s of FIXED_SETS) obj[r][s] = "";
  }
  return obj;
}

/* ========================
   VARSINAINEN WIZARD
   ======================== */

export default function PriceCalculatorWizard() {
  const { theme } = useTheme();

  // Teema-tyylit samoin kuin TietoaMeistä
  const cardBg   = theme === "dark" ? "bg-[#0b0b0f]/60 ring-white/10" : "bg-lemon/60 ring-white/40";
  const bodyText = theme === "dark" ? "text-white/85" : "text-black/80";
  const titleTxt = theme === "dark" ? "text-white" : "text-darkest";

  const hasMapsKey = !!process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const { isLoaded, loadError } = useJsApiLoader(
    hasMapsKey
      ? { id: GMAPS_LOADER_ID, googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY, libraries: GMAPS_LIBS }
      : { id: `${GMAPS_LOADER_ID}-disabled`, libraries: [] }
  );
  useEffect(() => { if (loadError) console.error("Google Maps load error:", loadError?.message); }, [loadError]);

  // heti useJsApiLoaderin jälkeen
const mapsReady =
  hasMapsKey &&
  isLoaded &&
  typeof window !== "undefined" &&
  !!window.google &&
  !!window.google.maps;


  const originACRef = useRef(null);
  const destACRef   = useRef(null);

  // Hinnat (LS)
  const [pricing, setPricing] = useState(createEmptyPriceMap());
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && parsed.pricing) {
        const merged = createEmptyPriceMap();
        for (const r of Object.keys(parsed.pricing)) {
          if (!merged[r]) continue;
          for (const s of Object.keys(parsed.pricing[r] || {})) {
            merged[r][s] = String(parsed.pricing[r][s] ?? "");
          }
        }
        setPricing(merged);
      }
    } catch {}
  }, []);
  useEffect(() => { localStorage.setItem(LS_KEY, JSON.stringify({ pricing })); }, [pricing]);

  const onChangePrice = useCallback((regionKey, setKey, raw) => {
    const v = String(raw).replace(",", ".");
    setPricing((prev) => ({ ...prev, [regionKey]: { ...prev[regionKey], [setKey]: v } }));
  }, []);

  // Vaiheet
  const [step, setStep] = useState(1); // 1: Osoitteet, 2: Setti & hinta, 3: Tulos

  // Osoitteet & etäisyys
  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  const [destRegion, setDestRegion] = useState("");
  const [distanceKm, setDistanceKm] = useState(null);
  const [distanceText, setDistanceText] = useState("");
  const [durationText, setDurationText] = useState("");
  const [busyDistance, setBusyDistance] = useState(false);

  // Setti + hinta (määräytyy kohteen maakunnan mukaan)
  const currentRegion = destRegion || "";
  const regionRow = useMemo(() => (pricing[currentRegion] ?? {}), [pricing, currentRegion]);
  const pricedSets = useMemo(
    () => Object.entries(regionRow).filter(([,v]) => numOrNull(v) != null).map(([k]) => k),
    [regionRow]
  );
  const [selectedSet, setSelectedSet] = useState("");
  const selectedBasePrice = String(regionRow?.[selectedSet] ?? "");

  // Uusi setti tälle maakunnalle
  const [addNewOpen, setAddNewOpen] = useState(false);
  const [newSetLabel, setNewSetLabel] = useState("");
  const [newSetPrice, setNewSetPrice] = useState("");

  // Tulos
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);

  /* ===== Google helpers ===== */
  const extractRegionFromPlace = (place) => {
    const comps = place?.address_components || [];
    const get = (type) => comps.find((c) => Array.isArray(c.types) && c.types.includes(type))?.long_name || "";
    const raw = get("administrative_area_level_1") || get("administrative_area_level_2") || "";
    return toCanon(raw);
  };

  const onOriginPlaceChanged = () => {
    const place = originACRef.current?.getPlace?.();
    if (!place) return;
    setOrigin(place.formatted_address || place.name || "");
  };
  const onDestPlaceChanged = () => {
    const place = destACRef.current?.getPlace?.();
    if (!place) return;
    setDestination(place.formatted_address || place.name || "");
    const detected = extractRegionFromPlace(place);
    setDestRegion(detected);
    if (selectedSet && numOrNull(pricing[detected]?.[selectedSet]) == null) setSelectedSet("");
  };

  const getDistanceMatrix = useCallback(() => {
    return new Promise((resolve, reject) => {
      const noGoogle = typeof window === "undefined" || !window.google;
      if (noGoogle) return reject(new Error("Google Maps ei ole saatavilla"));
      const service = new window.google.maps.DistanceMatrixService();
      service.getDistanceMatrix(
        { origins: [origin], destinations: [destination], travelMode: window.google.maps.TravelMode.DRIVING, unitSystem: window.google.maps.UnitSystem.METRIC },
        (response, status) => {
          if (status !== "OK") return reject(new Error("Google API status: " + status));
          const el = response?.rows?.[0]?.elements?.[0];
          if (!el || el.status !== "OK") return reject(new Error("Etäisyyttä ei saatu: " + (el?.status || "UNKNOWN")));
          const km = el.distance.value / 1000;
          resolve({ km, distanceText: el.distance.text, durationText: el.duration.text });
        }
      );
    });
  }, [origin, destination]);

  // Automaattinen haku kun molemmat osoitteet on annettu
useEffect(() => {
  if (!origin || !destination || !mapsReady) return;
  const t = setTimeout(async () => {
    setBusyDistance(true);
    setErr("");
    try {
      const res = await getDistanceMatrix(); // tämä jo tarkistaa window.google
      setDistanceKm(res.km);
      setDistanceText(res.distanceText || "");
      setDurationText(res.durationText || "");
    } catch (e) {
      setDistanceKm(null);
      setDistanceText("");
      setDurationText("");
      setErr(e?.message || "Etäisyyden haku epäonnistui.");
    } finally {
      setBusyDistance(false);
    }
  }, 400);
  return () => clearTimeout(t);
}, [origin, destination, mapsReady, getDistanceMatrix]);


  /* ===== Step handlers ===== */
  const goToStep2 = () => {
    setErr("");
    if (!destination || !origin) return setErr("Syötä ensin sekä lähtöpiste että tapahtumapaikka.");
    if (!currentRegion) return setErr("Kohteen maakuntaa ei voitu tunnistaa. Valitse tarkempi osoite.");
    setStep(2);
  };

  const addNewSetNow = () => {
    const label = newSetLabel.trim();
    const price = numOrNull(newSetPrice);
    if (!label) return setErr("Anna setille nimi.");
    if (price == null) return setErr("Anna kelvollinen perushinta (€).");
    setErr("");
    setPricing((prev) => ({
      ...prev,
      [currentRegion]: { ...(prev[currentRegion] || {}), [label]: String(price) },
    }));
    setSelectedSet(label);
    setAddNewOpen(false);
    setNewSetLabel("");
    setNewSetPrice("");
  };

  const handleCalculate = () => {
    setErr("");
    setResult(null);

    if (!currentRegion) return setErr("Kohteen maakunta puuttuu.");
    if (!selectedSet) return setErr("Valitse setti.");
    const base = numOrNull(pricing?.[currentRegion]?.[selectedSet]);
    if (base == null) return setErr("Tälle setille ei ole määritelty perushintaa.");
    if (distanceKm == null || distanceKm < 0) return setErr("Etäisyys puuttuu tai on virheellinen.");

    const kmTotal = Math.round(KM_PRICE * (distanceKm * 2));
    const total   = Math.round(base + kmTotal);

    setResult({
      region: currentRegion,
      duration: selectedSet,
      base_price: base,
      km_price: KM_PRICE,
      calc_distance_km: distanceKm,
      km_total: kmTotal,
      total,
      currency: "€",
      distanceText,
      durationText,
      origin,
      destination,
    });
    setStep(3);
  };

  /* ========================
         RENDER
     ======================== */

  return (
    <div className={cx("rounded-2xl backdrop-blur-md shadow-lg ring-1 p-6", cardBg)}>
      <h2 className={cx("mb-2 font-customFont2 dark:text-white/90 text-black/90 text-lg xxxsm:text-sm xxsm:text-sm s24:text-sm xsm:text-sm sm:text-lg md:text-lg mdd:text-lg font-semibold", titleTxt)}>
  Hinnoittelulaskuri
</h2>




      {/* Vaihepalkki */}
      <ol className="mb-6 flex items-center gap-2 text-xs">
        <li className={cx("rounded-full px-2 py-1", step === 1 ? "bg-black text-white dark:bg-white dark:text-black" : "bg-black/10 dark:bg-white/10")}>1. Osoitteet</li>
        <li className={cx("rounded-full px-2 py-1", step === 2 ? "bg-black text-white dark:bg-white dark:text-black" : "bg-black/10 dark:bg-white/10")}>2. Setti & hinta</li>
        <li className={cx("rounded-full px-2 py-1", step === 3 ? "bg-black text-white dark:bg-white dark:text-black" : "bg-black/10 dark:bg-white/10")}>3. Tulos</li>
      </ol>

      {/* STEP 1: Osoitteet */}
      {step === 1 && (
        <div className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="block text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm text-black/80 dark:text-white/80">Lähtöpiste</label>
              {mapsReady ? (
                <Autocomplete
                  onLoad={(a) => (originACRef.current = a)}
                  onPlaceChanged={onOriginPlaceChanged}
                  options={{ componentRestrictions: { country: "fi" }, fields: ["formatted_address","name","address_components"] }}
                >
                  <input
                    type="text"
                    placeholder="Kirjoita osoite / paikka…"
                    value={origin}
                    onChange={(e) => setOrigin(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-black/10 bg-white px-3 py-2 outline-none focus:ring-2 focus:ring-black/20 dark:border-white/10
                    
                    dark:bg-black dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm
                  dark:focus:ring-white/20"
                  />
                </Autocomplete>
              ) : (
                <input
                  type="text"
                  placeholder="Kirjoita osoite / paikka…"
                  value={origin}
                  onChange={(e) => setOrigin(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-black/10 bg-white px-3 py-2 dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm
                  outline-none focus:ring-2 focus:ring-black/20 dark:border-white/10 dark:bg-black dark:focus:ring-white/20"
                />
              )}
            </div>

            <div>
              <label className="block text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm text-black/80 dark:text-white/80">Tapahtumapaikka</label>
              {mapsReady ? (
                <Autocomplete
                  onLoad={(a) => (destACRef.current = a)}
                  onPlaceChanged={onDestPlaceChanged}
                  options={{ componentRestrictions: { country: "fi" }, fields: ["formatted_address","name","address_components"] }}
                >
                  <input
                    type="text"
                    placeholder="Kirjoita osoite / paikka…"
                    value={destination}
                    onChange={(e) => setDestination(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-black/10 bg-white px-3 py-2 dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm
                    outline-none focus:ring-2 focus:ring-black/20 dark:border-white/10 dark:bg-black  dark:focus:ring-white/20"
                  />
                </Autocomplete>
              ) : (
                <input
                  type="text"
                  placeholder="Kirjoita osoite / paikka…"
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-black/10 bg-white px-3 py-2 dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm
                  outline-none focus:ring-2 focus:ring-black/20 dark:border-white/10 dark:bg-black  dark:focus:ring-white/20"
                />
              )}

              {destRegion && (
                <p className="mt-2 dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm
                opacity-70">Tunnistettu maakunta: <b>{destRegion}</b></p>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <NeonButton
              onClick={goToStep2}
              disabled={!origin || !destination || !currentRegion || busyDistance}
              variant="primary"
              size="sm"
              IconLeft={MapPin}
            >
              Jatka settiin & hintaan
            </NeonButton>
          </div>

          {err && <div className="rounded-lg bg-red-500/10 p-3 text-sm text-red-700 dark:text-red-400">{err}</div>}
        </div>
      )}

      {/* STEP 2: Setti & hinta */}
      {step === 2 && (
        <div className="space-y-6">
          <div className={cx("rounded-lg p-3 text-sm font-customFont1 text-black/80 dark:text-white/80 dark:text-shadow-lg", theme === "dark" ? "bg-white/5" : "bg-black/5")}>
            Esiintymisalue: <b>{currentRegion || "–"}</b>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="md:col-span-2">
              <label className="block dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm
              ">Esiintymisen pituus</label>
              <select
                value={selectedSet}
                onChange={(e) => setSelectedSet(e.target.value)}
                className="mt-1 w-full rounded-lg border border-black/10 bg-white px-3 py-2 dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm
                outline-none focus:ring-2 focus:ring-black/20 dark:border-white/10 dark:bg-black dark:focus:ring-white/20"
              >
                <option value="">Valitse</option>
                {pricedSets.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <p className="mt-1 opacity-70 dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm
">
                Lista näyttää vain ne setit, joille on tälle maakunnalle hinta.
              </p>
            </div>

            <div className="md:col-span-1">
              <label className="dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm
              ">Perushinta (€)</label>
              <input
                type="text"
                value={selectedBasePrice}
                onChange={(e) => {
                  if (!selectedSet) return;
                  onChangePrice(currentRegion, selectedSet, e.target.value);
                }}
                placeholder={selectedSet ? "esim. 900" : "valitse setti ensin"}
                disabled={!selectedSet}
                className={cx(
                  "mt-1 w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2",
                  "border-black/10 bg-white focus:ring-black/20 dark:border-white/10 dark:bg-black font-customFont1 text-black/80 dark:text-white/80 dark:text-shadow-lg dark:focus:ring-white/20",
                  !selectedSet && "opacity-60"
                )}
              />
            </div>
          </div>

          <div className="rounded-xl border border-dashed border-black/15 p-3 dark:border-white/15">
            <button
              type="button"
              onClick={() => setAddNewOpen((o) => !o)}
              className="rounded-lg border border-black/10 px-3 py-2 dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm
            hover:bg-black/5 dark:border-white/10 dark:hover:bg-white/10"
            >
              {addNewOpen ? "Peruuta" : "Lisää uusi setti tälle maakunnalle"}
            </button>

            {addNewOpen && (
              <div className="mt-3 grid gap-3 md:grid-cols-3">
                <div className="md:col-span-2">
                  <label className="block text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm text-black/80 dark:text-white/80">Setin nimi</label>
                  <input
                    type="text"
                    value={newSetLabel}
                    onChange={(e) => setNewSetLabel(e.target.value)}
                    placeholder="esim. 1x75min"
                    className="mt-1 w-full rounded-lg border border-black/10 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black/20 dark:border-white/10 dark:bg-black font-customFont1 text-black/80 dark:text-white/80 dark:text-shadow-lg dark:focus:ring-white/20"
                  />
                </div>
                <div>
                  <label className="block dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm
                  ">Perushinta (€)</label>
                  <input
                    type="text"
                    value={newSetPrice}
                    onChange={(e) => setNewSetPrice(e.target.value)}
                    placeholder="esim. 1000"
                    className="mt-1 w-full rounded-lg border border-black/10 bg-white px-3 py-2 outline-none focus:ring-2 focus:ring-black/20 dark:border-white/10 dark:bg-black dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm
                  dark:focus:ring-white/20"
                  />
                </div>
                <div className="md:col-span-3">
                  <NeonButton onClick={addNewSetNow} variant="primary" size="sm" IconLeft={Calculator}>
                    Tallenna uusi setti
                  </NeonButton>
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <NeonButton
              onClick={handleCalculate}
              disabled={!currentRegion || !selectedSet || numOrNull(selectedBasePrice) == null || distanceKm == null}
              variant="primary"
              size="sm"
              IconLeft={Calculator}
            >
              Laske hinta
            </NeonButton>

            <button
              type="button"
              onClick={() => setStep(1)}
              className="rounded-xl border border-black/10 px-4 py-2 dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm
            hover:bg-black/5 dark:border-white/10 dark:hover:bg-white/10"
            >
              Takaisin osoitteisiin
            </button>
          </div>

          {err && <div className="rounded-lg bg-red-500/10 p-3 text-sm text-red-700 dark:text-red-400">{err}</div>}
        </div>
      )}

      {/* STEP 3: Tulos */}
      {step === 3 && result && (
        <div className="space-y-6">
          <div className="rounded-xl border border-black/10 p-4 dark:border-white/10">
            <h3 className={cx("mb-2 text-base font-semibold font-customFont2 text-black/80 dark:text-white/80 dark:text-shadow-lg", titleTxt)}>Erittely</h3>
            <dl className={cx("grid grid-cols-1 gap-2 text-sm font-customFont1 text-black/80 dark:text-white/80 dark:text-shadow-lgmd:grid-cols-2", bodyText)}>
              <div className="flex items-center justify-between gap-3 rounded-lg bg-black/5 px-3 py-2 dark:bg-white/5"><dt>Maakunta</dt><dd>{result.region}</dd></div>
              <div className="flex items-center justify-between gap-3 rounded-lg bg-black/5 px-3 py-2 dark:bg-white/5"><dt>Setti</dt><dd>{result.duration}</dd></div>
              <div className="flex items-center justify-between gap-3 rounded-lg bg-black/5 px-3 py-2 dark:bg-white/5"><dt>Lähtöpiste</dt><dd>{result.origin || "–"}</dd></div>
              <div className="flex items-center justify-between gap-3 rounded-lg bg-black/5 px-3 py-2 dark:bg-white/5"><dt>Tapahtumapaikka</dt><dd>{result.destination || "–"}</dd></div>
              <div className="flex items-center justify-between gap-3 rounded-lg bg-black/5 px-3 py-2 dark:bg-white/5"><dt>Etäisyys (yksisuunta)</dt><dd>{result.calc_distance_km != null ? `${result.calc_distance_km.toFixed(1)} km` : "–"}</dd></div>
              <div className="flex items-center justify-between gap-3 rounded-lg bg-black/5 px-3 py-2 dark:bg-white/5"><dt>Km-hinta</dt><dd>{`${result.km_price}${result.currency}/km`}</dd></div>
              <div className="flex items-center justify-between gap-3 rounded-lg bg-black/5 px-3 py-2 dark:bg-white/5"><dt>Kilometrikorvaus/matkulut</dt><dd>{`${result.km_total}${result.currency}`}</dd></div>
              <div className="flex items-center justify-between gap-3 rounded-lg bg-black/5 px-3 py-2 dark:bg-white/5"><dt>Perushinta</dt><dd>{`${result.base_price}${result.currency}`}</dd></div>
            </dl>

            <div className="mt-4 flex items-center justify-between rounded-xl bg-black px-4 py-3 font-customFont1 text-black/80 dark:text-white/80 dark:text-shadow-lg">
              <span className="text-base font-bold font-customFont1 text-white/80 dark:text-white/80 dark:text-shadow-lg">Yhteensä</span>
              <span className="text-2xl font-extrabold tracking-tight font-customFont1 text-pink/80 dark:text-pink/80 dark:text-shadow-lg">
                {`${result.total}${result.currency}`}
              </span>
            </div>

            {result?.durationText ? (
              <p className="mt-2 font-customFont1 text-black/80 dark:text-white/80 dark:text-shadow-lg text-base font-bold opacity-70">
                Ajoaika noin: {result.durationText}
              </p>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => setStep(2)}
              className="rounded-xl border border-black/10 px-4 py-2 dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm
            hover:bg-black/5 dark:border-white/10 dark:hover:bg-white/10"
            >
              Takaisin
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
